<script lang="ts" setup>
import { computed, PropType } from 'vue';
import { ComputeTaskUtils } from '@/utils/compute-utils';
import { ComputeTaskType } from '@/gql/graphql';
import { BBadge, BTooltip } from 'bootstrap-vue-next';
import { COMPUTE_TASK_STATUS } from '@/constants';
import ProgressIndicatorThreeDots from '../ProgressIndicatorThreeDots.vue';

// --- Component Properties ---
const props = defineProps({
  task: { type: Object as PropType<ComputeTaskType> },
  iconOnly: { type: Boolean, default: false },
});

// --- Component State ---
const errorMessage = computed(() =>
  props.task ? ComputeTaskUtils.errorMessage(props.task) : ''
);
const percentage = computed<number | null>(() => {
  if (!props.task) return null;
  const v = ComputeTaskUtils.progressPercentage(props.task);
  return typeof v === 'number' ? Math.round(v) : null;
});

const variant = computed(() => {
  if (!props.task) return 'secondary';
  return ComputeTaskUtils.progressVariant(props.task.status);
});

const isActive = computed(() => {
  if (!props.task) return false;
  return ComputeTaskUtils.progressActive(props.task.status);
});
</script>
<template>
  <template v-if="props.task">
    <!-- Status / Progress Text Description / Error Message -->
    <template v-if="props.iconOnly">
      <!-- make the icon color based on the variant -->
      <span :id="`task-status-${props.task.id}`" class="status-cell">
        <!-- badge with progress percentage if percentage is available -->
        <span
          v-if="percentage"
          :id="`task-status-${props.task.id}`"
          class="status-circle"
          :class="[`text-${variant}`, { pulse: isActive }]"
          aria-hidden="false"
          role="img"
        >
          <span class="status-circle-text">{{ percentage }}%</span>
        </span>
        <!-- status icon if percentage is not available -->
        <font-awesome-icon
          v-else
          :icon="ComputeTaskUtils.statusIcon(props.task.status)"
          :class="[
            ComputeTaskUtils.statusIconClass(props.task.status),
            `text-${ComputeTaskUtils.progressVariant(props.task.status)}`,
          ]"
          class="status-icon"
        />
      </span>
      <b-tooltip
        :target="`task-status-${props.task.id}`"
        triggers="hover focus"
        class="large-tooltip"
      >
        <span v-if="props.task?.status === COMPUTE_TASK_STATUS.RUNNING">
          {{
            ComputeTaskUtils.progressTooltipText(props.task) ||
            props.task.status
          }}
        </span>
        <span
          v-else-if="
            props.task?.status === COMPUTE_TASK_STATUS.FAILED && errorMessage
          "
        >
          <span>{{ props.task.status }}</span>
          <span v-if="errorMessage">: {{ errorMessage }}</span>
        </span>
        <span v-else>
          {{ props.task.status }}
        </span>
        <!-- Progress indicator if applicable -->
        <ProgressIndicatorThreeDots
          v-if="isActive"
          class="ms-1"
          style="font-size: 0.25rem; vertical-align: middle"
        />
      </b-tooltip>
    </template>
    <!-- Badge with full-blown Status / Progress Text Description / Error Message -->
    <template v-else>
      <div
        v-if="
          props.task?.status === COMPUTE_TASK_STATUS.RUNNING &&
          ComputeTaskUtils.progressTooltipText(props.task)
        "
      >
        <b-badge :variant="variant" class="text-wrap text-break text-start">
          <font-awesome-icon
            :icon="ComputeTaskUtils.statusIcon(props.task.status)"
            :class="
              ComputeTaskUtils.statusIconClass(props.task.status).concat([
                'me-1',
              ])
            "
          />
          <span>
            <strong v-if="ComputeTaskUtils.progressPercentage(props.task)">
              {{ ComputeTaskUtils.progressPercentage(props.task) }}%
            </strong>
            {{ ComputeTaskUtils.progressTooltipText(props.task) }}
          </span>
        </b-badge>
      </div>
      <div
        v-else-if="
          props.task?.status === COMPUTE_TASK_STATUS.FAILED && errorMessage
        "
      >
        <b-badge
          :variant="ComputeTaskUtils.progressVariant(props.task.status)"
          class="text-wrap text-break text-start"
        >
          <font-awesome-icon
            :icon="ComputeTaskUtils.statusIcon(props.task.status)"
            :class="
              ComputeTaskUtils.statusIconClass(props.task.status).concat([
                'me-1',
              ])
            "
          />
          <span>{{ props.task.status }}</span>
          <span v-if="errorMessage">: {{ errorMessage }}</span>
        </b-badge>
      </div>
      <div v-else>
        <b-badge
          :variant="ComputeTaskUtils.progressVariant(props.task.status)"
          class="text-wrap text-break text-start"
        >
          <font-awesome-icon
            :icon="ComputeTaskUtils.statusIcon(props.task.status)"
            :class="
              ComputeTaskUtils.statusIconClass(props.task.status).concat([
                'me-1',
              ])
            "
          />
          <span>{{ props.task.status }}</span>
        </b-badge>
      </div>
    </template>
  </template>
</template>
<style scoped>
/* fixed “icon” cell so rows align no matter icon vs % */
.status-cell {
  display: inline-flex;
  width: 1.5em; /* similar to .fa-fw footprint */
  min-width: 1.5em;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
}

/* circular % badge */
.status-circle {
  --size: 1.5em; /* tweak to taste */
  width: var(--size);
  height: var(--size);
  background-color: rgba(255, 255, 255, 0.5);
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  font-size: 1em;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  box-shadow: inset 0 0 0 1px rgba(13, 110, 253, 0.16);
  transition: all 0.2s ease-in-out;
}

.status-circle-text {
  font-size: 0.5em;
  transition: all 0.2s ease-in-out;
}

.status-circle:hover .status-circle-text {
  font-size: 0.75em;
}

.status-icon {
  font-size: 1em;
  vertical-align: middle;
}

/* gentle pulse while active */
@keyframes pulse {
  0% {
    transform: scale(1);
    box-shadow: inset 0 0 0 1px rgba(13, 110, 253, 0.16),
      0 0 0 0 rgba(13, 110, 253, 0);
  }
  50% {
    transform: scale(1.03);
    box-shadow: inset 0 0 0 1px rgba(13, 110, 253, 0.16),
      0 0 0 0.15rem rgba(13, 110, 253, 0.16);
  }
  100% {
    transform: scale(1);
    box-shadow: inset 0 0 0 1px rgba(13, 110, 253, 0.16),
      0 0 0 0 rgba(13, 110, 253, 0);
  }
}
.pulse {
  animation: pulse 2s ease-in-out infinite;
}
@media (prefers-reduced-motion: reduce) {
  .pulse {
    animation: none;
  }
}
</style>
